import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Ban, ChevronRight, EyeOff, Loader2, PlayCircle, Repeat, X } from 'lucide-react';

import './storefront-pages.css';
import { loadStorefrontClicks, loadStorefrontPageSnapshot, loadStorefrontView } from './api';
import { Empty, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import { KpiRow, NUMBER, Panel, Share } from './MobileParts';
import { loadPlayer } from './StorefrontReplay';
import { errorMessage } from './ui';
import {
  PathText, ReadState, StoreTable, StorefrontGate, StorefrontHead, count, deviceLabel, duration, ratio, st,
  stCount, useStorefrontSite,
} from './StorefrontParts';

// Every page of the store: how often it is opened, how long people stay, how
// far they scroll and where they leave, and for one page at a time, where
// people click: a heat map drawn over the page itself, as a recorded visitor
// on that kind of device saw it, and the things they click, named by the
// words a visitor sees on them.

const DEVICES = ['desktop', 'tablet', 'mobile'];
// The tallest the visitor's screen is drawn; the rest of the page scrolls under it.
const MAX_SCREEN = 620;
// How far one click's heat reaches, in pixels of the recorded page, and at least on screen.
const HEAT_RADIUS = 34;
const MIN_RADIUS = 14;
// The browser's limit on a canvas side is a little over 32,000 pixels.
const MAX_CANVAS = 30000;
const BANDS = [25, 50, 75];
const SHOWN = 12;
// Cool where a few people clicked, hot where many did.
const RAMP = [[0, [59, 76, 192, 0]], [0.1, [59, 110, 230, 105]], [0.3, [32, 176, 226, 150]], [0.5, [72, 196, 96, 185]],
  [0.7, [250, 204, 40, 210]], [0.86, [245, 122, 36, 228]], [1, [216, 38, 38, 240]]];
// A page part named by its landmark rather than a heading: the tracker's names for them.
const LANDMARKS = {
  navigation: 'menu', nav: 'menu', banner: 'header', header: 'header', contentinfo: 'footer', footer: 'footer',
  complementary: 'aside', aside: 'aside', dialog: 'popup', alertdialog: 'popup', search: 'search',
};

function rampColor(value) {
  for (let i = 1; i < RAMP.length; i += 1) {
    if (value <= RAMP[i][0]) {
      const [from, a] = RAMP[i - 1];
      const [to, b] = RAMP[i];
      const t = (value - from) / (to - from);
      return a.map((channel, index) => Math.round(channel + (b[index] - channel) * t));
    }
  }
  return RAMP[RAMP.length - 1][1];
}

const RAMP_CSS = `linear-gradient(90deg, ${RAMP.slice(1).map(([at, [r, g, b]]) => `rgb(${r}, ${g}, ${b}) ${Math.round(at * 100)}%`).join(', ')})`;

// The layer drawn into the replayed document, and the frame's own rules: it
// scrolls by itself with no scrollbar taking width from the page, and nothing in
// the page answers a pointer (a click cannot follow a link out of the replay).
const LAYER = 'xo-heat-layer';
const FRAME_RULES = `html * { pointer-events: none !important; cursor: default !important; }
html { cursor: default; user-select: none; scrollbar-width: none; }
html::-webkit-scrollbar { display: none; }`;
// While its stylesheets and pictures arrive a page grows: its height is read again for a few seconds.
const MEASURE_MS = 400;
const MEASURE_ROUNDS = 15;
const LABEL_FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

// The clicks as heat, on a canvas the size of the page (one canvas pixel per page
// pixel): each spot soft and faint on its own, so that colour comes from clicks
// piling up in one place, then coloured from cool to hot. A click is placed by the
// window it was made in: across as a share of its width, down in widths of it
// (`yw`), so that phones 390 and 412 pixels wide land on the same spot.
function paintHeat(canvas, points, width, radius) {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.clearRect(0, 0, canvas.width, canvas.height);
  const most = Math.max(1, ...points.map((point) => point.count));
  for (const point of points) {
    const x = point.x * width;
    const y = point.yw * width;
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, `rgba(0,0,0,${Math.min(1, 0.14 + (point.count / most) * 0.5)})`);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    context.fillStyle = gradient;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = image.data;
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] === 0) continue;
    const [r, g, b, a] = rampColor(pixels[i] / 255);
    pixels[i - 3] = r;
    pixels[i - 2] = g;
    pixels[i - 1] = b;
    pixels[i] = a;
  }
  context.putImageData(image, 0, 0);
}

// One element of the layer, with every style of its own (`all: initial`), so no
// rule of the store's page reaches it.
function part(doc, tag, style) {
  const element = doc.createElement(tag);
  element.style.cssText = `all: initial; display: block; position: absolute; ${style}`;
  return element;
}

// The heat, the end of the first screen and the scroll bands, in one layer of the
// replayed document at page coordinates, over the page's whole height: they
// scroll with the page natively, as one surface. The frame is drawn smaller than
// the page (`scale`), so lines and labels are sized in screen pixels (`unit`).
function drawLayer(doc, { width, screenHeight, pageHeight, points, scroll, showHeat, scale }) {
  let layer = doc.querySelector(LAYER);
  if (!layer) {
    layer = doc.createElement(LAYER);
    doc.documentElement.appendChild(layer);
  }
  const unit = 1 / scale;
  layer.style.cssText = `all: initial; display: block; position: absolute; left: 0; top: 0; width: ${width}px; height: ${pageHeight}px;
    overflow: hidden; pointer-events: none; z-index: 2147483647;${showHeat ? ' background: rgba(16, 24, 48, 0.14);' : ''}`;
  layer.replaceChildren();
  if (showHeat && points.length > 0) {
    // Painted on a canvas of the portal's own and shown in the page as a picture: the
    // replay runs no scripts, and a browser leaves a canvas blank in such a page.
    const tall = Math.min(pageHeight, MAX_CANVAS);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(tall));
    paintHeat(canvas, points, width, Math.max(MIN_RADIUS * unit, HEAT_RADIUS));
    const heat = part(doc, 'img', `left: 0; top: 0; width: ${width}px; height: ${tall}px;`);
    heat.alt = '';
    heat.src = canvas.toDataURL('image/png');
    layer.appendChild(heat);
  }
  const band = (top, text, fold) => {
    const line = part(doc, 'xo-heat-band', `left: 0; right: 0; top: ${top}px; height: 0;
      border-top: ${1.5 * unit}px dashed rgba(16, 33, 75, ${fold ? 0.4 : 0.6});`);
    const label = part(doc, 'xo-heat-label', `right: ${8 * unit}px; top: ${(fold ? -26 : -11) * unit}px; padding: ${2 * unit}px ${8 * unit}px;
      border-radius: ${999 * unit}px; font: 600 ${11 * unit}px/1.35 ${LABEL_FONT}; white-space: nowrap;
      ${fold ? `background: #fff; color: #10214b; border: ${unit}px solid #d9dfeb;` : 'background: rgba(16, 33, 75, 0.86); color: #fff;'}`);
    label.textContent = text;
    line.appendChild(label);
    layer.appendChild(line);
  };
  const clear = 16 * unit;
  if (pageHeight > screenHeight + clear) band(screenHeight, st('pages.first_screen'), true);
  for (const depth of BANDS) {
    const entry = scroll.find((row) => row.depth === depth);
    const top = (depth / 100) * pageHeight;
    if (entry && entry.share !== null && top > screenHeight + clear) band(top, st('pages.reached_here', { share: ratio(entry.share) }), false);
  }
}

// The page as a visitor saw it: the recording's first full snapshot, rebuilt by
// rrweb's player in a sandboxed frame that runs no scripts, and held still. The
// frame keeps the visitor's screen size, so the page lays out as it did for them
// (a hero as tall as the screen stays so), and scrolls by itself; the heat and the
// bands are drawn into the page (drawLayer), so nothing has to follow the scroll.
// The layer is drawn again only for another device's page, a resize, or new data.
// Stylesheets and pictures load from the store itself, as they do in a replay.
function RecordedPage({ snapshot, points, scroll, showHeat }) {
  const { events } = snapshot;
  const meta = events.find((event) => event.type === 4);
  const screenW = (meta && meta.data && meta.data.width) || (snapshot.viewport && snapshot.viewport.w) || 1280;
  const screenH = (meta && meta.data && meta.data.height) || (snapshot.viewport && snapshot.viewport.h) || 800;
  const [state, setState] = useState({ status: 'loading', error: null });
  const [pageHeight, setPageHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const box = useRef(null);
  const root = useRef(null);
  const player = useRef(null);
  const scale = width > 0 ? Math.min(1, width / screenW, MAX_SCREEN / screenH) : 0;
  const shownW = screenW * scale;
  const shownH = screenH * scale;

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => setWidth(Math.floor(entries[0].contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    let replayer = null;
    let timer = 0;
    setState({ status: 'loading', error: null });
    setPageHeight(0);
    loadPlayer().then((Replayer) => {
      if (!live) return;
      root.current.replaceChildren();
      replayer = new Replayer(events, {
        root: root.current, showWarning: false, showDebug: false, triggerFocus: false, mouseTail: false, skipInactive: false,
      });
      player.current = replayer;
      replayer.pause(replayer.getMetaData().totalTime);
      const doc = replayer.iframe.contentDocument;
      const rules = doc.createElement('style');
      rules.textContent = FRAME_RULES;
      (doc.head || doc.documentElement).appendChild(rules);
      replayer.enableInteract();
      replayer.iframe.contentWindow.scrollTo(0, 0);
      // The page's own height, without the layer over it.
      const measure = () => {
        const page = doc.scrollingElement || doc.documentElement;
        if (!page) return;
        const layer = doc.querySelector(LAYER);
        if (layer) layer.style.display = 'none';
        const height = page.scrollHeight;
        if (layer) layer.style.display = 'block';
        setPageHeight((before) => (before === height ? before : height));
      };
      measure();
      let rounds = 0;
      timer = window.setInterval(() => {
        rounds += 1;
        measure();
        if (rounds >= MEASURE_ROUNDS) window.clearInterval(timer);
      }, MEASURE_MS);
      setState({ status: 'ready', error: null });
    }).catch((error) => {
      if (live) setState({ status: 'failed', error: errorMessage(error) });
    });
    return () => {
      live = false;
      window.clearInterval(timer);
      player.current = null;
      if (replayer) {
        try { replayer.destroy(); } catch { /* already torn down */ }
      }
    };
  }, [events]);

  useEffect(() => {
    const replayer = player.current;
    if (!replayer || !replayer.wrapper || scale <= 0) return;
    replayer.wrapper.style.transform = `scale(${scale})`;
    replayer.wrapper.style.transformOrigin = 'top left';
  }, [scale, state.status]);

  useEffect(() => {
    const replayer = player.current;
    if (state.status !== 'ready' || !replayer || !replayer.iframe || scale <= 0 || pageHeight <= 0) return;
    drawLayer(replayer.iframe.contentDocument, { width: screenW, screenHeight: screenH, pageHeight, points, scroll, showHeat, scale });
  }, [state.status, scale, pageHeight, points, scroll, showHeat, screenW, screenH]);

  return (
    <div className="xo-sfp-map" ref={box}>
      <div className="xo-sfp-stage" style={{ width: shownW || '100%', height: shownH || 320 }}>
        <div ref={root} className="xo-sfp-root" />
        {state.status === 'loading' && <span className="xo-sfp-note"><Loader2 className="xo-spin" aria-hidden="true" />{st('pages.drawing')}</span>}
        {state.status === 'failed' && <span className="xo-sfp-note is-bad">{state.error}</span>}
      </div>
      <p className="xo-mob-footnote xo-sfp-caption">
        {st('pages.recorded_note', {
          date: new Date(snapshot.startedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
          width: screenW, height: screenH,
        })} {st('pages.scroll_hint')}
      </p>
    </div>
  );
}

function NoRecording({ device }) {
  return (
    <div className="xo-sfp-empty">
      <EyeOff aria-hidden="true" />
      <strong>{st(`pages.no_recording_${device}`)}</strong>
      <span>{st('pages.no_recording_detail')}</span>
    </div>
  );
}

function placeName(place) {
  if (!place) return null;
  const landmark = LANDMARKS[place.toLowerCase()];
  return landmark ? st(`pages.place_${landmark}`) : place;
}

// What people click, named as a visitor sees it: the words on it (in English,
// from the store's translations when it has a key), the part of the page it is
// in, and where a link leads; each as its share of the page's clicks.
function ClickList({ elements }) {
  const [all, setAll] = useState(false);
  const total = elements.reduce((sum, element) => sum + element.clicks, 0);
  if (elements.length === 0) return <Empty title={st('pages.no_clicks')} />;
  const shown = all ? elements : elements.slice(0, SHOWN);
  return (
    <>
      <ol className="xo-sfp-clicks">
        {shown.map((element, index) => {
          const what = element.label || st(`pages.kind_${element.kind || 'text'}`);
          const place = placeName(element.place);
          const where = place && place !== what ? place : null;
          return (
            <li key={`${what}-${where}-${index}`}>
              <div className="xo-sfp-click-head">
                <span className="xo-sfp-click-name" title={where ? st('pages.click_name', { what, where }) : what}>
                  <strong>{what}</strong>{where && <span>{st('pages.click_where', { where })}</span>}
                </span>
                <span className="xo-sfp-click-count"><b>{ratio(total > 0 ? element.clicks / total : null)}</b><small>{stCount('pages.click_count', element.clicks)}</small></span>
              </div>
              <Share value={element.clicks} total={total} />
              {(element.destination || element.rageClicks > 0 || element.deadClicks > 0) && (
                <div className="xo-sfp-click-notes">
                  {element.destination && <span>{st('pages.goes_to', { page: element.destination })}</span>}
                  {element.rageClicks > 0 && <em className="is-rage"><Repeat aria-hidden="true" />{stCount('pages.rage_plain', element.rageClicks)}</em>}
                  {element.deadClicks > 0 && <em className="is-dead"><Ban aria-hidden="true" />{stCount('pages.dead_plain', element.deadClicks)}</em>}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {elements.length > SHOWN && (
        <button type="button" className="xo-mob-link xo-sfp-more" onClick={() => setAll(!all)}>
          {all ? st('pages.show_fewer') : st('pages.show_all', { count: NUMBER.format(elements.length) })}
        </button>
      )}
    </>
  );
}

function DeviceMap({ site, days, path, device, showHeat }) {
  const loadClicks = useCallback(() => loadStorefrontClicks({ site: site.code, days, path, device }), [site.code, days, path, device]);
  const clicks = useLiveResource(['store_traffic'], loadClicks, { reconcileMs: 0 });
  const loadSnapshot = useCallback(() => loadStorefrontPageSnapshot({ site: site.code, days, path, device }), [site.code, days, path, device]);
  const snapshot = useLiveResource([], loadSnapshot, { reconcileMs: 0 });
  const data = clicks.data;
  return (
    <div className="xo-sfp-layout">
      <div className="xo-sfp-main">
        {data && (
          <p className="xo-sfp-summary">
            {st(`pages.heat_summary_${device}`, { clicks: NUMBER.format(data.clicks), visits: NUMBER.format(data.pageSessions) })}
          </p>
        )}
        {snapshot.data === null ? <ReadState resource={snapshot} label={st('pages.drawing')} />
          : snapshot.data.snapshot === null ? <NoRecording device={device} />
            : <RecordedPage snapshot={snapshot.data.snapshot} points={data ? data.points : []} scroll={data ? data.scroll : []} showHeat={showHeat} />}
      </div>
      <div className="xo-sfp-side">
        <h3 className="xo-mob-subhead">{st('pages.elements_title')}</h3>
        <p className="xo-sfp-side-note">{st('pages.elements_note')}</p>
        {data === null ? <ReadState resource={clicks} label={st('pages.map_reading')} /> : <ClickList elements={data.elements} />}
      </div>
    </div>
  );
}

function ClickMap({ site, days, path, onClose }) {
  const [picked, setPicked] = useState(null);
  const [showHeat, setShowHeat] = useState(true);
  // Every device's clicks at once, for how many visits each kind of device made.
  const loadAll = useCallback(() => loadStorefrontClicks({ site: site.code, days, path }), [site.code, days, path]);
  const all = useLiveResource(['store_traffic'], loadAll, { reconcileMs: 0 });
  const visitsBy = useMemo(() => new Map((all.data ? all.data.widths : []).map((entry) => [entry.deviceType, entry.sessions])), [all.data]);
  // The kind of device most visits came from, until someone picks another.
  const device = picked || (all.data ? [...DEVICES].sort((a, b) => (visitsBy.get(b) || 0) - (visitsBy.get(a) || 0))[0] : null);

  return (
    <Panel title={st('pages.map_title', { path })} className="xo-sfp-panel" detail={st('pages.heat_intro')}
      aside={<button type="button" className="xo-mob-icon-button" onClick={onClose} aria-label={st('close')}><X /></button>}>
      <div className="xo-sfp-bar">
        <WorkspaceTabs value={device || ''} onChange={setPicked} label={st('pages.device')} className="is-compact xo-mob-range"
          tabs={DEVICES.map((value) => ({ value, label: deviceLabel(value), count: all.data ? visitsBy.get(value) || 0 : undefined }))} />
        <span className="xo-sfp-legend">
          <small>{st('pages.legend_few')}</small>
          <i style={{ background: RAMP_CSS }} aria-hidden="true" />
          <small>{st('pages.legend_many')}</small>
        </span>
        <label className="xo-sfp-toggle">
          <input type="checkbox" checked={showHeat} onChange={(event) => setShowHeat(event.target.checked)} />
          {st('pages.show_heat')}
        </label>
        <Link className="xo-mob-link xo-sfp-watch" to={`/xomics/store/sessions?path=${encodeURIComponent(path)}&replay=true`}>
          <PlayCircle aria-hidden="true" />{st('pages.watch')}
        </Link>
      </div>
      {device === null ? <ReadState resource={all} label={st('pages.map_reading')} />
        : <DeviceMap key={device} site={site} days={days} path={path} device={device} showHeat={showHeat} />}
    </Panel>
  );
}

function PagesView({ site, store }) {
  const [days, setDays] = useState(30);
  const [searchParams, setSearchParams] = useSearchParams();
  const picked = searchParams.get('path');
  const load = useCallback(() => loadStorefrontView('pages', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const pick = useCallback((path) => {
    const next = new URLSearchParams(searchParams);
    if (path) next.set('path', path); else next.delete('path');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const pages = useMemo(() => (report.data ? report.data.pages : []), [report.data]);
  const head = <StorefrontHead title={st('pages.title')} subtitle={st('pages.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('pages.loading')} /></div>;
  const views = pages.reduce((sum, page) => sum + page.views, 0);
  const slowest = [...pages].filter((page) => page.avgLoadMs !== null).sort((a, b) => b.avgLoadMs - a.avgLoadMs)[0];
  const leaky = [...pages].filter((page) => page.views >= 50 && page.exitRate !== null).sort((a, b) => b.exitRate - a.exitRate)[0];
  const mostViews = Math.max(1, ...pages.map((page) => page.views));
  const columns = [
    { key: 'path', label: st('pages.col_page'), primary: true, sortValue: (row) => row.path, render: (row) => <PathText path={row.path} title={row.title} /> },
    { key: 'views', label: st('pages.col_views'), align: 'end', sortValue: (row) => row.views,
      render: (row) => <span className="xo-sf-share-cell"><Share value={row.views} total={mostViews} /><b>{count(row.views)}</b></span> },
    { key: 'visitors', label: st('pages.col_visitors'), align: 'end', wide: true, sortValue: (row) => row.visitors, render: (row) => count(row.visitors) },
    { key: 'entries', label: st('pages.col_entries'), align: 'end', wide: true, sortValue: (row) => row.entries, render: (row) => count(row.entries) },
    { key: 'exitRate', label: st('pages.col_exit'), align: 'end', sortValue: (row) => row.exitRate, render: (row) => ratio(row.exitRate) },
    { key: 'active', label: st('pages.col_active'), align: 'end', sortValue: (row) => row.avgActiveMs, render: (row) => duration(row.avgActiveMs) },
    { key: 'scroll', label: st('pages.col_scroll'), align: 'end', wide: true, sortValue: (row) => row.avgScrollPct, render: (row) => (row.avgScrollPct === null ? st('none') : `${row.avgScrollPct}%`) },
    { key: 'load', label: st('pages.col_load'), align: 'end', wide: true, sortValue: (row) => row.avgLoadMs, render: (row) => duration(row.avgLoadMs) },
    { key: 'clicks', label: st('pages.col_clicks'), align: 'end', wide: true, sortValue: (row) => row.clicks, render: (row) => count(row.clicks) },
    { key: 'frustration', label: st('pages.col_frustration'), align: 'end', sortValue: (row) => row.rageClicks + row.deadClicks + row.errors,
      render: (row) => (row.rageClicks + row.deadClicks + row.errors === 0 ? <span className="is-muted">{st('none')}</span> : (
        <span className="xo-sf-frustration">
          {row.rageClicks > 0 && <em className="is-rage" title={stCount('pages.rage_count', row.rageClicks)}>{row.rageClicks}</em>}
          {row.deadClicks > 0 && <em className="is-dead" title={stCount('pages.dead_count', row.deadClicks)}>{row.deadClicks}</em>}
          {row.errors > 0 && <em className="is-error" title={stCount('errors_count', row.errors)}>{row.errors}</em>}
        </span>
      )) },
  ];
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <KpiRow items={[
        { label: st('pages.kpi_views'), value: count(views), detail: stCount('pages_count', pages.length) },
        { label: st('pages.kpi_leaky'), value: leaky ? <code className="xo-sf-kpi-code">{leaky.path}</code> : st('none'), detail: leaky ? st('pages.kpi_leaky_detail', { rate: ratio(leaky.exitRate) }) : null },
        { label: st('pages.kpi_slowest'), value: slowest ? <code className="xo-sf-kpi-code">{slowest.path}</code> : st('none'), detail: slowest ? st('pages.kpi_slowest_detail', { time: duration(slowest.avgLoadMs) }) : null },
      ]} />
      {picked && <ClickMap key={`${picked}-${days}`} site={site} days={days} path={picked} onClose={() => pick(null)} />}
      <Panel title={st('pages.list_title')} detail={st('pages.list_detail')}
        aside={!picked && pages[0] ? <button type="button" className="xo-mob-link" onClick={() => pick(pages[0].path)}>{st('pages.open_map')}<ChevronRight aria-hidden="true" /></button> : null}>
        <StoreTable columns={columns} rows={pages} rowKey={(row) => row.path} active={picked} onRow={(row) => { pick(row.path); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          initialSort={{ key: 'views', direction: 'desc' }} limit={25} empty={<Empty title={st('pages.none')} />} />
        <p className="xo-mob-footnote xo-sf-frustration-key">
          <span className="xo-sf-frustration"><em className="is-rage">{st('pages.key_rage')}</em><em className="is-dead">{st('pages.key_dead')}</em><em className="is-error">{st('pages.key_errors')}</em></span>
          {st('pages.frustration_note')}
        </p>
      </Panel>
    </div>
  );
}

export default function StorefrontPages() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <PagesView site={site} store={store} />}</StorefrontGate>;
}
